import { useState } from 'react';
import {
  ConfigurationType,
  DependenciesContentViewType,
  SelectedDependencyWithClearHistoryProps,
} from '../../../../documentation/dependencies/types';
import { useSweepNotifications } from '../../../../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../../../../reducers/notificationsReducer';
import { telemetry } from '../../../../../telemetry';
import { addConfigurationItems } from '../../../../../reducers/documentation/documentationReducer';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/material';
import { CenteredCircularProgress } from '../../../../common/CenteredCircularProgress';
import { DependenciesSideDialogContent } from '../../../../documentation/dependencies/DependenciesSideDialogContent';
import { useRunOnceWhenTruthy } from '../../../../common/useRunOnceWhenTruthy';
import { useDependencies } from '../../../../documentation/dependencies/hooks/useDependencies';
import { colors, IconButton } from '@sweep-io/sweep-design';
import { HEADER_TOP_ENDORNMENT } from '../../../../documentation/dependencies/helpers';
import { Close } from '@sweep-io/sweep-design/dist/icons';
import { UniversalSearchProvider } from '../../../../documentation/universal-search/UniversalSearchContext';
import useParser from '../../../../parser/useParser';
import { AddToCanvasButton } from '../metadata-elements/AddToCanvasButton';
import { isMetadataElementInCanvas } from '../metadata-elements/helper';
import { slideFromLeft } from '../../../../animations/cssAnimations';
import { DocumentationFiltersProvider } from '../../../../documentation/selected-object/filters/DocumentationFiltersContext';
import { useFetchConfigurationItem } from './useFetchConfigurationItem';
import { UI_EVENTS } from '../../../../../services/events';

interface DependenciesPanelProps {
  metadataElementId: string;
  crmOrgId: string;
  onClose: () => void;
  color?: string;
  canvasFunnelMap: FunnelMap;
}

const DependenciesPanel = ({
  metadataElementId,
  crmOrgId,
  onClose,
  color,
  canvasFunnelMap,
}: DependenciesPanelProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { addNotification } = useSweepNotifications();
  const { handleSetConfigurationItem, configurationItem } = useDependencies(crmOrgId);
  const { fetchConfigurationItem } = useFetchConfigurationItem();
  const { parseObjectIfNotParsed } = useParser({ crmOrgId });
  const _configurationItem = configurationItem ? { ...configurationItem } : undefined; //make configurationItem mutable

  useRunOnceWhenTruthy(async () => {
    setIsLoading(true);

    try {
      const item = await fetchConfigurationItem(metadataElementId, crmOrgId);
      if (item.parentType === ConfigurationType.objects) {
        await parseObjectIfNotParsed(item.name);
      }

      //This transformation is mainly to add "clearHistory" and "contentType", which are UI only props
      const transformed: SelectedDependencyWithClearHistoryProps = {
        id: item.id,
        parentType: item.parentType as ConfigurationType,
        dependencyType: item.type,
        name: item.name,
        objectName: item.objectName,
        clearHistory: true,
        contentType: DependenciesContentViewType.default,
      };

      handleSetConfigurationItem(transformed);

      dispatch(
        addConfigurationItems({
          crmOrgId,
          newConfigurationItems: { [item.parentType]: [item] },
        }),
      );
    } catch (err) {
      telemetry.captureError(err);
      addNotification({
        variant: SweepNotificationVariant.Error,
        message: 'Failed to load metadata element',
      });
      onClose();
    } finally {
      setIsLoading(false);
    }
  }, !!metadataElementId);

  const type = _configurationItem?.parentType as ConfigurationType;
  const showLoader = isLoading || !_configurationItem || !type;
  const isInCanvas = isMetadataElementInCanvas({
    crmOrgId,
    metadataId: _configurationItem?.metadataId ?? '',
    canvasFunnelMap,
  });

  if (_configurationItem) {
    const _color = color && color.toLowerCase() !== '#ffffff' ? color : undefined;
    _configurationItem.color = _color;
  }

  return (
    <Box
      sx={{
        position: 'absolute' as const,
        left: 0,
        top: 0,
        borderRadius: 0,
        bottom: 0,
        height: '100%',
        boxShadow: 1,
        width: '464px',
        backgroundColor: colors.white,
        zIndex: 101, //to be on top of the layer selector
        ...slideFromLeft,
      }}
    >
      {showLoader && (
        <>
          <Box
            sx={{
              width: '100%',
              height: HEADER_TOP_ENDORNMENT,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              pr: 1,

              button: {
                '&:hover': {
                  backgroundColor: 'transparent',
                  overflow: 'hidden',

                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    height: '100%',
                    width: '100%',
                    backgroundColor: colors.grey[120],
                    mixBlendMode: 'soft-light',
                    zIndex: '1',
                  },
                },
              },
            }}
          >
            <IconButton variant="flat" size="tiny" onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            height={`calc(100% - ${HEADER_TOP_ENDORNMENT})`}
            width="100%"
          >
            <CenteredCircularProgress />
          </Box>
        </>
      )}
      {!showLoader && (
        <UniversalSearchProvider>
          <DependenciesSideDialogContent
            crmOrgId={crmOrgId}
            configurationItem={_configurationItem}
            type={type}
            onClose={onClose}
            isReadOnly={true}
            headerContent={
              <AddToCanvasButton
                isStaticView={true}
                isInCanvas={isInCanvas}
                element={_configurationItem}
                callback={onClose}
                crmOrgId={crmOrgId}
                biEventName={UI_EVENTS.canvasAddMetadataToCanvas}
              />
            }
          />
        </UniversalSearchProvider>
      )}
    </Box>
  );
};

const DependenciesPanelWithContext = (props: DependenciesPanelProps) => (
  <DocumentationFiltersProvider>
    <DependenciesPanel {...props} />
  </DocumentationFiltersProvider>
);

export { DependenciesPanelWithContext as DependenciesPanel };
